'use client';

import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Link as ChakraLink,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CheckCircle2, ChevronRight, Circle, ListChecks, ShieldCheck } from 'lucide-react';
import NextLink from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { getGrowthActivityFeedLabels } from '@/app/app/home/growth-activity-labels';
import { LoadingIndicator } from '@/components/shared/LoadingIndicator';
import type { GrowthDecisionRecord } from '@/lib/attune/growth/growth-decisions';
import {
  publishWorkspaceApprovalPendingCount,
  subscribeDeliveryApprovalsChanged,
} from '@/lib/delivery-approvals/client-events';
import { isDeliveryApprovalsEnabledClient } from '@/lib/delivery-approvals/feature-flags';
import type {
  DeliveryApprovalRecord,
  DeliveryApprovalStatus,
  DeliveryProvider,
} from '@/lib/delivery-approvals/types';
import type { GrowthProgramProposalRecord } from '@/types/growth-program';
import type { GrowthWorkspaceActivityEvent } from '@/types/growth-workspace';
import { formatCodexRelativeTime, titleLabel } from '@/lib/codex-workspace-format';
import { getWorkspaceWorkPresentation } from '@/lib/workspace-home-status';

type GrowthIssueStatus =
  | 'open'
  | 'claimed'
  | 'in_progress'
  | 'submitted'
  | 'measuring'
  | 'verified'
  | 'closed'
  | 'dismissed'
  | 'regression_candidate';

type GrowthIssueOutcome =
  | 'helped'
  | 'no_movement'
  | 'regressed'
  | 'unknown'
  | 'regression_candidate';

interface GrowthIssueRecord {
  id: string;
  title: string;
  problem_statement: string;
  recommended_action: string;
  status: GrowthIssueStatus;
  severity: 'high' | 'medium' | 'low';
  priority_score: number;
  program_id?: string;
  program_revision?: number;
  bet_id?: string;
  intervention_id?: string;
  manager_priority_score?: number;
  dependency_issue_ids?: string[];
  category: string;
  source: {
    scan_run_id?: string;
    scan_automation_id?: string;
    job_id?: string;
  };
  evidence: {
    summary?: string;
    marketer_role?: string;
    asset_type?: string;
    asset_id?: string;
    acceptance_criteria?: string;
    verification_plan?: string;
    confidence?: 'low' | 'medium' | 'high';
    lane_tags?: string[];
    affected_urls?: string[];
    affected_domains?: string[];
    evidence_keys: string[];
    prompt_ids: string[];
  };
  owner?: {
    type: 'user' | 'agent' | 'system' | 'unassigned';
    name: string;
    uid?: string;
    email?: string;
    run_id?: string;
  };
  outcome?: GrowthIssueOutcome;
  latest_verification?: {
    outcome: GrowthIssueOutcome;
    comparison_scan_run_id?: string;
    confidence: 'low' | 'medium' | 'high';
    explanation: string;
    recommended_next_step?: string;
  };
  detection_count: number;
  last_detected_at: string | null;
  updated_at: string | null;
}

type GrowthActivityEventRecord = GrowthWorkspaceActivityEvent & {
  actor?: {
    type: 'user' | 'agent' | 'system';
    name: string;
    uid?: string;
    email?: string;
    run_id?: string;
  };
  issue?: GrowthWorkspaceActivityEvent['issue'] & {
    problem_statement?: string;
    recommended_action?: string;
    outcome?: GrowthIssueOutcome;
    latest_verification?: GrowthIssueRecord['latest_verification'];
    owner?: GrowthIssueRecord['owner'];
    evidence?: Pick<
      GrowthIssueRecord['evidence'],
      | 'summary'
      | 'marketer_role'
      | 'asset_type'
      | 'asset_id'
      | 'confidence'
      | 'lane_tags'
      | 'affected_urls'
      | 'affected_domains'
      | 'evidence_keys'
      | 'prompt_ids'
    >;
  };
  receipt_preview?: {
    summary: string;
    evidence?: string;
    changed_urls: string[];
    changed_files: string[];
    commit_urls: string[];
    pr_urls: string[];
    changed_url_count: number;
    changed_file_count: number;
    commit_url_count: number;
    pr_url_count: number;
  };
};

const ACTIVE_GROWTH_WORK_STATUSES = new Set<GrowthIssueStatus>([
  'open',
  'regression_candidate',
  'claimed',
  'in_progress',
  'submitted',
]);
const MEASURING_GROWTH_WORK_STATUSES = new Set<GrowthIssueStatus>(['measuring']);
const COMPLETED_GROWTH_WORK_STATUSES = new Set<GrowthIssueStatus>(['verified', 'closed']);
const HOME_APPROVAL_STATUSES: DeliveryApprovalStatus[] = [
  'pending',
  'executing',
  'stale',
  'reconciliation_required',
];
const HOME_APPROVAL_REFRESH_MS = 60_000;
const HOME_APPROVAL_MIN_REFRESH_GAP_MS = 5_000;

const HOME_APPROVAL_STATUS_LABELS: Partial<Record<DeliveryApprovalStatus, string>> = {
  pending: 'Needs review',
  executing: 'In progress',
  stale: 'Needs refresh',
  reconciliation_required: 'Needs attention',
};

const HOME_APPROVAL_PROVIDER_LABELS: Record<DeliveryProvider, string> = {
  webflow: 'Webflow',
  shopify: 'Shopify',
  wordpress: 'WordPress.com',
  github: 'GitHub',
};

const HOME_APPROVAL_PHASE_LABELS: Record<DeliveryApprovalRecord['phase'], string> = {
  apply_draft: 'Create draft',
  publish: 'Publish live',
  live_update: 'Update live content',
  rollback: 'Restore content',
};

interface HomeDeliveryApprovalListResponse {
  approvals: DeliveryApprovalRecord[];
  total: number;
  pendingCount: number;
}

interface HomeGrowthApprovalQueueResponse {
  configured?: boolean;
  pendingProposal?: GrowthProgramProposalRecord | null;
  pendingDecisions?: GrowthDecisionRecord[];
  pendingDecisionCount?: number;
  error?: string;
}

interface HomeAttentionItem {
  id: string;
  kind: 'growth_proposal' | 'growth_decision' | 'delivery';
  title: string;
  detail: string;
  statusLabel: string;
  createdAt: string | null;
  href: string;
  priority: number;
}

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (
    status === 'enabled' ||
    status === 'completed' ||
    status === 'verified' ||
    status === 'closed'
  ) {
    return 'success';
  }
  if (
    status === 'paused' ||
    status === 'submitted' ||
    status === 'measuring' ||
    status === 'queued' ||
    status === 'running'
  ) {
    return 'warning';
  }
  if (status === 'failed' || status === 'regressed' || status === 'dismissed') {
    return 'danger';
  }
  if (status === 'open' || status === 'claimed' || status === 'in_progress') {
    return 'info';
  }
  return 'neutral';
}

function dotColorForTone(tone: ReturnType<typeof statusTone>) {
  if (tone === 'success') return 'status.success.fg';
  if (tone === 'warning') return 'status.warning.fg';
  if (tone === 'danger') return 'status.danger.fg';
  if (tone === 'info') return 'status.info.fg';
  return 'ui.text.tertiary';
}

function homeTaskOutcome(issue: GrowthIssueRecord) {
  return issue.latest_verification?.outcome || issue.outcome || null;
}

function WidgetMessage({ children }: { children: ReactNode }) {
  return (
    <Flex align="center" justify="center" minH="156px" h="full" p="4">
      <Text fontSize="sm" color="ui.text.secondary" textAlign="center">
        {children}
      </Text>
    </Flex>
  );
}

function WidgetLoading({ label }: { label: string; animated?: boolean }) {
  return (
    <VStack role="status" align="center" minH="156px" h="full" justify="center" gap="2.5" p="4" aria-busy="true">
      <LoadingIndicator decorative />
      <Text fontSize="xs" color="ui.text.tertiary" textAlign="center">
        {label}
      </Text>
    </VStack>
  );
}

function AttentionQueueLoading() {
  return (
    <HStack minH="48px" px="2" gap="3" role="status" aria-busy="true">
      <LoadingIndicator decorative />
      <Text fontSize="xs" color="ui.text.tertiary">Loading…</Text>
    </HStack>
  );
}

function WorkspaceWidgetShell({
  icon,
  title,
  href,
  actionLabel,
  children,
}: {
  icon: ReactNode;
  title: string;
  href: string;
  actionLabel: string;
  children: ReactNode;
}) {
  return (
    <Box
      data-section="workspaceHomeWidget"
      layerStyle="surfacePanel"
      bg="ui.surface.card"
      boxShadow="none"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="12px"
      overflow="hidden"
      minH="0"
      h="full"
      display="flex"
      flexDirection="column"
    >
      <Flex
        justify="space-between"
        align="center"
        px="4"
        py="2.5"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        gap="1.5"
        flexWrap="wrap"
        flexShrink={0}
      >
        <HStack gap="2" minW={0} flex="1">
          <Icon boxSize="3.5" color="ui.text.secondary" flexShrink={0}>
            {icon}
          </Icon>
          <Text fontSize="sm" color="ui.text.primary" fontWeight="600" lineClamp={1}>
            {title}
          </Text>
        </HStack>
        <HStack gap="1" flexShrink={0}>
          <ChakraLink
            as={NextLink}
            href={href}
            display="inline-flex"
            alignItems="center"
            minH="28px"
            px="1.5"
            borderRadius="glassInput"
            fontSize="xs"
            color="ui.text.secondary"
            whiteSpace="nowrap"
            _hover={{ color: 'ui.text.primary', bg: 'ui.surface.selected', textDecoration: 'none' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'ui.border.strong',
              outlineOffset: '2px',
            }}
          >
            {actionLabel}
          </ChakraLink>
        </HStack>
      </Flex>
      <Box flex="1" minH="0">
        {children}
      </Box>
    </Box>
  );
}

function RecentWorkList({
  events,
  loading,
  loadError = false,
  onOpenActivityEvent,
  excludeLabel,
}: {
  events: GrowthActivityEventRecord[];
  loading: boolean;
  loadError?: boolean;
  onOpenActivityEvent?: (event: GrowthActivityEventRecord) => void;
  excludeLabel?: string;
}) {
  const visibleEvents = useMemo(() => {
    const seen = new Set<string>();
    const normalizedExcludedLabel = excludeLabel?.trim().toLowerCase() || '';
    return events
      .filter(event => {
        const labels = getGrowthActivityFeedLabels(event);
        const key = labels.primary.trim().toLowerCase();
        if (normalizedExcludedLabel && key === normalizedExcludedLabel) return false;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 4);
  }, [events, excludeLabel]);

  return (
    <VStack align="stretch" gap="0" flex="1" minH="184px">
      {loading && visibleEvents.length === 0 ? (
        <WidgetLoading label="Loading recent work…" animated />
      ) : visibleEvents.length === 0 ? (
        <Flex minH="68px" align="center">
          <Text fontSize="xs" color="ui.text.secondary">
            {loadError ? 'Recent activity is unavailable.' : 'Your task updates will appear here.'}
          </Text>
        </Flex>
      ) : (
        visibleEvents.map((event, index) => {
          const labels = getGrowthActivityFeedLabels(event);
          const presentation = getWorkspaceWorkPresentation(
            event.issue?.status || '',
            event.issue?.latest_verification?.outcome || event.issue?.outcome
          );
          return (
            <HStack
              as="button"
              key={event.id || `${event.type}-${event.created_at}-${index}`}
              align="center"
              gap="2"
              w="full"
              flex={{ base: 'none', lg: '1' }}
              minH="40px"
              textAlign="left"
              bg="transparent"
              appearance="none"
              px="1"
              py="1.5"
              borderTopWidth={index === 0 ? '0' : '1px'}
              borderLeftWidth="0"
              borderRightWidth="0"
              borderBottomWidth="0"
              borderColor="ui.border.subtle"
              borderRadius="6px"
              cursor={onOpenActivityEvent ? 'pointer' : 'default'}
              _hover={onOpenActivityEvent ? { bg: 'glass.fill' } : undefined}
              _active={{ transform: 'scale(0.99)' }}
              transition="background-color 150ms ease, transform 120ms ease-out"
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'ui.border.strong',
                outlineOffset: '-2px',
              }}
              onClick={() => onOpenActivityEvent?.(event)}
            >
              <Icon boxSize="3" color={dotColorForTone(presentation.tone)}>
                {presentation.tone === 'success' ? <CheckCircle2 /> : <Circle />}
              </Icon>
              <Text
                flex="1"
                minW={0}
                fontSize="xs"
                color="ui.text.primary"
                fontWeight="500"
                lineClamp={index === 0 ? 2 : 1}
              >
                {labels.primary}
              </Text>
              {event.created_at ? (
                <Text fontSize="2xs" color="ui.text.tertiary" whiteSpace="nowrap" flexShrink={0}>
                  {formatCodexRelativeTime(event.created_at)}
                </Text>
              ) : null}
            </HStack>
          );
        })
      )}
    </VStack>
  );
}

function homeApprovalTitle(approval: DeliveryApprovalRecord): string {
  const label =
    approval.selectableTargets.find(target => target.selected)?.label ||
    approval.selectableTargets[0]?.label;
  if (!label?.trim()) {
    return `${HOME_APPROVAL_PROVIDER_LABELS[approval.provider]} delivery`;
  }
  return label
    .trim()
    .replace(
      /\b[a-f0-9]{8}-[a-f0-9]{4}-[1-5a-f0-9][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}\b/gi,
      'Prepared content'
    )
    .replace(/\b[a-f0-9]{24,}\b/gi, 'Prepared content')
    .slice(0, 120);
}

export function WorkspaceApprovalsPanel({
  user,
  projectId,
  aiCmoEnabled,
  onAiCmoStatusResolved,
  onAiCmoStatusLoadError,
}: {
  user: User | null;
  projectId: string | null;
  aiCmoEnabled: boolean;
  onAiCmoStatusResolved?: (configured: boolean) => void;
  onAiCmoStatusLoadError?: () => void;
}) {
  const deliveryApprovalsEnabled = isDeliveryApprovalsEnabledClient();
  const enabled = deliveryApprovalsEnabled || aiCmoEnabled;
  const [deliveryApprovals, setDeliveryApprovals] = useState<DeliveryApprovalRecord[]>([]);
  const [deliveryPendingCount, setDeliveryPendingCount] = useState(0);
  const [pendingGrowthProposal, setPendingGrowthProposal] =
    useState<GrowthProgramProposalRecord | null>(null);
  const [pendingGrowthDecisions, setPendingGrowthDecisions] = useState<GrowthDecisionRecord[]>([]);
  const [pendingGrowthDecisionCount, setPendingGrowthDecisionCount] = useState(0);
  const [loading, setLoading] = useState(enabled);
  const [deliveryLoadError, setDeliveryLoadError] = useState(false);
  const [growthLoadError, setGrowthLoadError] = useState(false);
  const approvalRequestSequence = useRef(0);
  const deliverySnapshotRef = useRef('');
  const growthSnapshotRef = useRef('');

  const loadApprovals = useCallback(
    async (signal?: AbortSignal, quiet = false) => {
      const requestSequence = ++approvalRequestSequence.current;
      if (!enabled || !user || !projectId) {
        deliverySnapshotRef.current = '';
        growthSnapshotRef.current = '';
        setDeliveryApprovals(current => (current.length > 0 ? [] : current));
        setDeliveryPendingCount(0);
        setPendingGrowthProposal(current => (current ? null : current));
        setPendingGrowthDecisions(current => (current.length > 0 ? [] : current));
        setPendingGrowthDecisionCount(0);
        setLoading(false);
        return;
      }
      if (!quiet) setLoading(true);
      setDeliveryLoadError(false);
      setGrowthLoadError(false);
      try {
        const token = await user.getIdToken();
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        const [deliveryResult, growthResult] = await Promise.allSettled([
          deliveryApprovalsEnabled
            ? fetch('/api/attune/delivery-approvals/list', {
                method: 'POST',
                headers,
                signal,
                body: JSON.stringify({
                  projectId,
                  statuses: HOME_APPROVAL_STATUSES,
                  limit: 4,
                }),
              }).then(async response => {
                const payload = (await response.json().catch(() => ({}))) as
                  | HomeDeliveryApprovalListResponse
                  | { error?: string };
                if (!response.ok) throw new Error('Unable to load delivery approvals');
                return payload as HomeDeliveryApprovalListResponse;
              })
            : Promise.resolve(null),
          aiCmoEnabled
            ? fetch('/api/attune/growth-programs/approval-queue', {
                method: 'POST',
                headers,
                signal,
                body: JSON.stringify({ projectId, compact: true }),
              }).then(async response => {
                const payload = (await response.json().catch(() => ({}))) as
                  | HomeGrowthApprovalQueueResponse
                  | { error?: string };
                if (!response.ok) {
                  throw new Error(
                    'error' in payload && payload.error
                      ? payload.error
                      : 'Unable to load AI CMO approvals'
                  );
                }
                return payload as HomeGrowthApprovalQueueResponse;
              })
            : Promise.resolve(null),
        ]);
        if (signal?.aborted || requestSequence !== approvalRequestSequence.current) return;

        if (deliveryResult.status === 'fulfilled') {
          const deliveryPayload = deliveryResult.value;
          const approvals = deliveryPayload?.approvals || [];
          const count = Math.max(
            0,
            Number(deliveryPayload?.total ?? deliveryPayload?.pendingCount) || 0
          );
          const snapshot = JSON.stringify({ approvals, count });
          if (snapshot !== deliverySnapshotRef.current) {
            deliverySnapshotRef.current = snapshot;
            setDeliveryApprovals(approvals);
            setDeliveryPendingCount(count);
          }
        } else {
          deliverySnapshotRef.current = '';
          setDeliveryApprovals(current => (current.length > 0 ? [] : current));
          setDeliveryPendingCount(0);
          setDeliveryLoadError(true);
        }

        if (growthResult.status === 'fulfilled') {
          const growthPayload = growthResult.value;
          const decisions = growthPayload?.pendingDecisions || [];
          if (typeof growthPayload?.configured === 'boolean') {
            onAiCmoStatusResolved?.(growthPayload.configured);
          } else if (aiCmoEnabled) {
            onAiCmoStatusLoadError?.();
          }
          const proposal = growthPayload?.pendingProposal || null;
          const count = Math.max(
            decisions.length,
            Number(growthPayload?.pendingDecisionCount) || 0
          );
          const snapshot = JSON.stringify({ proposal, decisions, count });
          if (snapshot !== growthSnapshotRef.current) {
            growthSnapshotRef.current = snapshot;
            setPendingGrowthProposal(proposal);
            setPendingGrowthDecisions(decisions);
            setPendingGrowthDecisionCount(count);
          }
        } else {
          growthSnapshotRef.current = '';
          setPendingGrowthProposal(current => (current ? null : current));
          setPendingGrowthDecisions(current => (current.length > 0 ? [] : current));
          setPendingGrowthDecisionCount(0);
          setGrowthLoadError(true);
          onAiCmoStatusLoadError?.();
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (requestSequence !== approvalRequestSequence.current) return;
        deliverySnapshotRef.current = '';
        growthSnapshotRef.current = '';
        setDeliveryApprovals(current => (current.length > 0 ? [] : current));
        setDeliveryPendingCount(0);
        setPendingGrowthProposal(current => (current ? null : current));
        setPendingGrowthDecisions(current => (current.length > 0 ? [] : current));
        setPendingGrowthDecisionCount(0);
        setDeliveryLoadError(deliveryApprovalsEnabled);
        setGrowthLoadError(aiCmoEnabled);
        if (aiCmoEnabled) onAiCmoStatusLoadError?.();
      } finally {
        if (!signal?.aborted && requestSequence === approvalRequestSequence.current) {
          setLoading(false);
        }
      }
    },
    [
      aiCmoEnabled,
      deliveryApprovalsEnabled,
      enabled,
      onAiCmoStatusLoadError,
      onAiCmoStatusResolved,
      projectId,
      user,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    let inFlight: Promise<void> | null = null;
    let refreshQueued = false;
    let lastRefreshStartedAt = 0;
    const runRefresh = (quiet: boolean, force = false) => {
      if (!force && document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (inFlight) {
        if (force) refreshQueued = true;
        return;
      }
      if (!force && now - lastRefreshStartedAt < HOME_APPROVAL_MIN_REFRESH_GAP_MS) return;
      lastRefreshStartedAt = now;
      inFlight = loadApprovals(controller.signal, quiet).finally(() => {
        inFlight = null;
        if (refreshQueued && !controller.signal.aborted) {
          refreshQueued = false;
          runRefresh(true, true);
        }
      });
    };
    const refresh = () => runRefresh(true);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const refreshOnFocus = () => refresh();
    runRefresh(false, true);
    const interval = window.setInterval(() => refresh(), HOME_APPROVAL_REFRESH_MS);
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const unsubscribe = subscribeDeliveryApprovalsChanged(changedProjectId => {
      if (changedProjectId === projectId) runRefresh(true, true);
    });
    return () => {
      approvalRequestSequence.current += 1;
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      unsubscribe();
    };
  }, [loadApprovals, projectId]);

  const pendingCount =
    deliveryPendingCount + pendingGrowthDecisionCount + (pendingGrowthProposal ? 1 : 0);
  useEffect(() => {
    if (!enabled || !projectId || loading) return;
    publishWorkspaceApprovalPendingCount(projectId, pendingCount);
  }, [enabled, loading, pendingCount, projectId]);
  const loadError = deliveryLoadError || growthLoadError;
  const attentionItems = useMemo<HomeAttentionItem[]>(() => {
    const growthProposalItems: HomeAttentionItem[] = pendingGrowthProposal
      ? [
          {
            id: pendingGrowthProposal.id,
            kind: 'growth_proposal',
            title:
              typeof pendingGrowthProposal.normalized_proposal.title === 'string'
                ? pendingGrowthProposal.normalized_proposal.title
                : 'Review proposed Growth Plan',
            detail: 'AI CMO · Growth Plan approval',
            statusLabel: 'Needs review',
            createdAt: pendingGrowthProposal.updated_at || pendingGrowthProposal.created_at,
            href: '/app/workspace?mode=growth-program',
            priority: 1,
          },
        ]
      : [];
    const growthDecisionItems = pendingGrowthDecisions.map<HomeAttentionItem>(decision => ({
      id: decision.id,
      kind: 'growth_decision',
      title: decision.question || 'AI CMO needs a decision',
      detail: `AI CMO · ${titleLabel(decision.kind)}`,
      statusLabel: 'Needs review',
      createdAt: decision.updated_at || decision.created_at,
      href: '/app/workspace?mode=growth-program',
      priority: 2,
    }));
    const deliveryItems = deliveryApprovals.map<HomeAttentionItem>(approval => ({
      id: approval.id,
      kind: 'delivery',
      title: homeApprovalTitle(approval),
      detail: `${HOME_APPROVAL_PROVIDER_LABELS[approval.provider]} · ${
        HOME_APPROVAL_PHASE_LABELS[approval.phase]
      }`,
      statusLabel: HOME_APPROVAL_STATUS_LABELS[approval.status] || 'Needs review',
      createdAt: approval.createdAt,
      href: `/app/workspace?mode=tasks&tab=approvals&approvalId=${encodeURIComponent(approval.id)}`,
      priority: ['stale', 'reconciliation_required'].includes(approval.status) ? 0 : 3,
    }));

    return [...growthProposalItems, ...growthDecisionItems, ...deliveryItems]
      .sort((left, right) => {
        if (left.priority !== right.priority) return left.priority - right.priority;
        const rightTimestamp = Date.parse(right.createdAt || '');
        const leftTimestamp = Date.parse(left.createdAt || '');
        return (
          (Number.isFinite(rightTimestamp) ? rightTimestamp : 0) -
          (Number.isFinite(leftTimestamp) ? leftTimestamp : 0)
        );
      })
      .slice(0, 3);
  }, [deliveryApprovals, pendingGrowthDecisions, pendingGrowthProposal]);

  if (!enabled) return null;
  if (!loading && pendingCount === 0 && !loadError) return null;

  return (
    <Box
      data-section="workspaceAttentionQueue"
      layerStyle="surfacePanel"
      bg="ui.surface.panel"
      boxShadow="var(--attune-shadow-subtle, none)"
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="8px"
      overflow="hidden"
      minH="0"
    >
      <Flex
        align="center"
        justify="space-between"
        gap="2"
        px="3.5"
        py="2.5"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
      >
        <HStack gap="2" minW={0}>
          <Text fontSize="sm" fontWeight="800" color="ui.text.primary">
            Needs your attention
          </Text>
          {pendingCount > 0 ? (
            <Text
              minW="5"
              h="5"
              px="1.5"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="status.warning.bg"
              borderWidth="1px"
              borderColor="status.warning.border"
              fontSize="2xs"
              fontWeight="800"
              color="status.warning.fg"
              fontVariantNumeric="tabular-nums"
            >
              {pendingCount > 99 ? '99+' : pendingCount}
            </Text>
          ) : null}
        </HStack>
        <ChakraLink
          as={NextLink}
          href="/app/workspace?mode=tasks&tab=approvals"
          display="inline-flex"
          alignItems="center"
          gap="1"
          minH="7"
          px="2"
          borderRadius="glassInput"
          fontSize="xs"
          fontWeight="700"
          color="ui.text.secondary"
          flexShrink={0}
          _hover={{ color: 'ui.text.primary', bg: 'ui.surface.selected', textDecoration: 'none' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'ui.border.strong',
            outlineOffset: '2px',
          }}
        >
          Review all
          <ChevronRight size={14} />
        </ChakraLink>
      </Flex>

      <VStack align="stretch" gap="0" px="3" py="2">
        {loading && attentionItems.length === 0 ? (
          <AttentionQueueLoading />
        ) : loadError && attentionItems.length === 0 ? (
          <WidgetMessage>Approvals couldn’t be loaded right now.</WidgetMessage>
        ) : (
          <>
            {loadError ? (
              <Text px="2" py="2" fontSize="xs" color="status.warning.fg">
                Some approval sources couldn’t be loaded. Available items are still shown.
              </Text>
            ) : null}
            {attentionItems.map((item, index) => (
              <ChakraLink
                as={NextLink}
                key={`${item.kind}:${item.id}`}
                href={item.href}
                display="flex"
                alignItems="flex-start"
                gap="2.5"
                w="full"
                py="2"
                borderTopWidth={index === 0 ? '0' : '1px'}
                borderColor="ui.border.subtle"
                textDecoration="none"
                _hover={{ bg: 'glass.fill', textDecoration: 'none' }}
                _active={{ transform: 'scale(0.99)' }}
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: 'ui.border.strong',
                  outlineOffset: '-2px',
                }}
                transition="background-color 150ms ease, transform 120ms ease-out"
              >
                <Box
                  boxSize="4.5"
                  borderRadius="full"
                  bg="status.warning.bg"
                  borderWidth="1px"
                  borderColor="status.warning.border"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt="0.5"
                >
                  <Icon boxSize="2.75" color="status.warning.fg">
                    <ShieldCheck />
                  </Icon>
                </Box>
                <Box minW={0} flex="1">
                  <Flex align="start" justify="space-between" gap="2.5">
                    <Text
                      fontSize="13px"
                      lineHeight="1.35"
                      color="ui.text.primary"
                      fontWeight="760"
                      lineClamp={1}
                    >
                      {item.title}
                    </Text>
                    <Text
                      fontSize="11px"
                      color="status.warning.fg"
                      fontWeight="800"
                      whiteSpace="nowrap"
                      flexShrink={0}
                      lineHeight="1.35"
                    >
                      {item.statusLabel}
                    </Text>
                  </Flex>
                  <Text mt="0.5" fontSize="12px" color="ui.text.tertiary" lineClamp={1}>
                    {item.detail}
                    {item.createdAt ? ` · ${formatCodexRelativeTime(item.createdAt)}` : ''}
                  </Text>
                </Box>
              </ChakraLink>
            ))}
            {pendingCount > attentionItems.length ? (
              <Text px="2" py="2" fontSize="xs" color="ui.text.tertiary">
                Showing {attentionItems.length} of {pendingCount} items needing review.
              </Text>
            ) : null}
          </>
        )}
      </VStack>
    </Box>
  );
}

export function HomeWorkWidget({
  issues,
  loading,
  issuesLoadError = false,
  activityLoadError = false,
  events,
  activityLoading,
  onOpenIssue,
  onOpenActivityEvent,
}: {
  issues: GrowthIssueRecord[];
  loading: boolean;
  issuesLoadError?: boolean;
  activityLoadError?: boolean;
  events: GrowthActivityEventRecord[];
  activityLoading: boolean;
  onOpenIssue?: (issue: GrowthIssueRecord) => void;
  onOpenActivityEvent?: (event: GrowthActivityEventRecord) => void;
}) {
  const rowsByBucket = useMemo(() => {
    const updatedAt = (issue: GrowthIssueRecord) => {
      const parsed = Date.parse(issue.updated_at || issue.last_detected_at || '');
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const sortRows = (rows: GrowthIssueRecord[]) =>
      rows.sort((a, b) => {
        const hasManagerScore =
          Number.isFinite(a.manager_priority_score) || Number.isFinite(b.manager_priority_score);
        return (
          (hasManagerScore
            ? (b.manager_priority_score ?? b.priority_score) -
              (a.manager_priority_score ?? a.priority_score)
            : 0) ||
          updatedAt(b) - updatedAt(a) ||
          b.priority_score - a.priority_score
        );
      });

    return {
      open: sortRows(issues.filter(issue => ACTIVE_GROWTH_WORK_STATUSES.has(issue.status))),
      measuring: sortRows(issues.filter(issue => MEASURING_GROWTH_WORK_STATUSES.has(issue.status))),
      completed: sortRows(issues.filter(issue => COMPLETED_GROWTH_WORK_STATUSES.has(issue.status))),
    };
  }, [issues]);
  const focusIssue =
    rowsByBucket.open[0] || rowsByBucket.measuring[0] || rowsByBucket.completed[0] || null;
  const focusOutcome = focusIssue ? homeTaskOutcome(focusIssue) : null;
  const focusPresentation = focusIssue
    ? getWorkspaceWorkPresentation(focusIssue.status, focusOutcome)
    : null;
  const focusTone = focusPresentation?.tone || 'neutral';
  const focusLabel = focusPresentation?.label;
  const countsUnavailable = issues.length === 0 && (loading || issuesLoadError);

  return (
    <WorkspaceWidgetShell
      icon={<ListChecks />}
      title="Work"
      href="/app/workspace?mode=tasks&tab=activity"
      actionLabel="View activity"
    >
      {issuesLoadError || activityLoadError ? (
        <Text role="status" px="4" pt="3" fontSize="xs" color="status.warning.fg">
          {issuesLoadError && activityLoadError
            ? 'Work and activity updates couldn’t be loaded.'
            : issuesLoadError
              ? 'Work updates couldn’t be loaded.'
              : 'Activity updates couldn’t be loaded.'}{' '}
          Showing the latest available information.
        </Text>
      ) : null}
      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        gap="0"
        p="4"
        h={{ base: 'auto', lg: 'full' }}
        css={{
          '@container app-content (max-width: 47.9975rem)': {
            gridTemplateColumns: '1fr',
            height: 'auto',
          },
        }}
      >
        <Box minW={0} pr={{ lg: '3' }} display="flex" flexDirection="column">
          <Text fontSize="xs" color="ui.text.tertiary" mb="2">
            {countsUnavailable ? 'Recent tasks' : `From ${issues.length} recent tasks`}
          </Text>
          <SimpleGrid
            columns={3}
            gap="0"
            mb="2"
            borderWidth="1px"
            borderColor="ui.border.subtle"
            borderRadius="glassInput"
            bg="ui.surface.input"
            overflow="hidden"
          >
            {[
              { label: 'Active', count: rowsByBucket.open.length },
              { label: 'Measuring', count: rowsByBucket.measuring.length },
              { label: 'Completed', count: rowsByBucket.completed.length },
            ].map((metric, index) => (
              <HStack
                key={metric.label}
                justify="center"
                gap="1.5"
                minW={0}
                px="2"
                py="1.5"
                borderLeftWidth={index === 0 ? '0' : '1px'}
                borderColor="ui.border.subtle"
              >
                <Text
                  fontSize="sm"
                  lineHeight="1"
                  color="ui.text.primary"
                  fontWeight="700"
                  fontVariantNumeric="tabular-nums"
                >
                  {countsUnavailable ? '—' : metric.count}
                </Text>
                <Text fontSize="2xs" color="ui.text.secondary" fontWeight="700" lineClamp={1}>
                  {metric.label}
                </Text>
              </HStack>
            ))}
          </SimpleGrid>
          {loading && !focusIssue ? (
            <Flex flex="1" minH="60px" align="center" justify="center">
              <Text fontSize="xs" color="ui.text.tertiary">
                Loading current work…
              </Text>
            </Flex>
          ) : focusIssue ? (
            <Box
              as="button"
              w="full"
              flex="1"
              minH="60px"
              display="flex"
              alignItems="center"
              textAlign="left"
              borderWidth="1px"
              borderColor="ui.border.subtle"
              borderRadius="glassInput"
              bg="ui.surface.card"
              px="3"
              py="2"
              cursor={onOpenIssue ? 'pointer' : 'default'}
              aria-disabled={!onOpenIssue}
              _hover={onOpenIssue ? { bg: 'glass.fill' } : undefined}
              _active={onOpenIssue ? { transform: 'scale(0.99)' } : undefined}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'ui.border.strong',
                outlineOffset: '2px',
              }}
              transition="background-color 150ms ease, transform 120ms ease-out"
              onClick={() => onOpenIssue?.(focusIssue)}
            >
              <Flex w="full" align="start" justify="space-between" gap="3">
                <Box minW={0}>
                  <HStack gap="1.5">
                    <Box
                      boxSize="1.5"
                      borderRadius="full"
                      bg={dotColorForTone(focusTone)}
                      flexShrink={0}
                    />
                    <Text fontSize="xs" color="ui.text.secondary" fontWeight="600">
                      {focusLabel}
                    </Text>
                  </HStack>
                  <Text
                    mt="0.5"
                    fontSize="sm"
                    color="ui.text.primary"
                    fontWeight="600"
                    lineClamp={1}
                  >
                    {focusIssue.title}
                  </Text>
                </Box>
                <VStack align="flex-end" gap="1" flexShrink={0}>
                  <Text fontSize="xs" color="ui.text.secondary" fontWeight="600">
                    {titleLabel(focusIssue.status)}
                  </Text>
                  <Text fontSize="xs" color="ui.text.tertiary">
                    {formatCodexRelativeTime(focusIssue.updated_at || focusIssue.last_detected_at)}
                  </Text>
                </VStack>
              </Flex>
            </Box>
          ) : (
            <Flex flex="1" minH="60px" align="center" px="3">
              <Box>
                <Text fontSize="sm" color="ui.text.primary" fontWeight="600">
                  {issuesLoadError ? 'Current work is unavailable' : 'No recent tasks'}
                </Text>
                <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
                  {issuesLoadError
                    ? 'Refresh to try again.'
                    : 'Describe what you want to work on above.'}
                </Text>
              </Box>
            </Flex>
          )}
        </Box>
        <Box
          minW={0}
          mt={{ base: '3', lg: '0' }}
          pt={{ base: '3', lg: '0' }}
          pl={{ lg: '3' }}
          display="flex"
          flexDirection="column"
          borderTopWidth={{ base: '1px', lg: '0' }}
          borderLeftWidth={{ base: '0', lg: '1px' }}
          borderColor="ui.border.subtle"
        >
          <Text
            mb="1"
            fontSize="2xs"
            color="ui.text.tertiary"
            fontWeight="600"
            textTransform="uppercase"
          >
            Recent
          </Text>
          <RecentWorkList
            events={events}
            loading={activityLoading}
            loadError={activityLoadError}
            excludeLabel={focusIssue?.title}
            onOpenActivityEvent={onOpenActivityEvent}
          />
        </Box>
      </SimpleGrid>
    </WorkspaceWidgetShell>
  );
}
